import dayjs from 'dayjs';
import { runInAction } from 'mobx';
import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { popupsTasks } from '@gem/popups';

import Tasks from 'APP/Tasks';
import {
  CalendarType,
  IUpdateCalendarEventParams,
  IUpdateRecurrenceCalendarEventParams,
  ICreateCalendarEventParams,
  ReminderType,
  IEvent,
  ConfirmationType,
  IParticipant,
  ICalendarAuthAccount,
} from 'APP/model/calendar/calendar.types';
import { User } from 'APP/store/Users/User/User';

import { ActiveSidebarView, IEventCalendar } from '../Content.types';
import { RepeatEnum } from './Form/Form.constants';
import { ICalendarEventSubmitData } from './Form/Form.types';

interface IRightSidebarPresenter {
  title: string;
  hasBackButton: boolean;
  isInfo: boolean;
  isEdit: boolean;
  isCreate: boolean;
  goBack(): void;
  onClose(): void;
  onEdit(): void;
  onDelete(): void;
  onCreateEvent(params: ICalendarEventSubmitData): Promise<void>;
  onEditEvent(params: ICalendarEventSubmitData): Promise<void>;
  setActiveView(view: ActiveSidebarView): void;
  setEvent(selectedEvent: IEventCalendar): void;
  updateParticipantStatus(confirmationType: ConfirmationType): Promise<void>;
  getUsersByEmails(participants: IParticipant[]): Promise<void>;
  activeView: ActiveSidebarView;
  selectedEvent: IEventCalendar | null;
  participantsInfo: User[];
  accounts: ICalendarAuthAccount[];
  setAuthEmails(accounts: ICalendarAuthAccount[]): void;
}

interface IRightSidebarPresenterProps {
  onClose(): void;
  onInfo(): void;
  onEdit(): void;
  updateEventAfterUpdateParticipantStatusSuccess(event: IEventCalendar): void;
  addEventsAfterCreateRecurrenceSuccess(): void;
  addEventAfterCreateSuccess(newEvent: IEvent): void;
  deleteEventsAfterDeleteRecurrenceSuccess(id: string): void;
  deleteEventAfterDeleteSuccess(id: string): void;
  onEditEvent(
    event: IUpdateRecurrenceCalendarEventParams | IUpdateCalendarEventParams
  ): Promise<boolean>;
  activeView: ActiveSidebarView;
  selectedEvent: IEventCalendar | undefined;
  templateEvent: IEventCalendar | null;
  accounts: ICalendarAuthAccount[];
}

export function useRightSidebarPresenter(
  props: IRightSidebarPresenterProps
): IRightSidebarPresenter {
  const { t } = useTranslation();
  const TITLES = {
    [ActiveSidebarView.Info]: '',
    [ActiveSidebarView.Edit]: t('common_edit'),
    [ActiveSidebarView.Create]: t('calendar_event_create'),
  };

  const presenter = useLocalObservable<IRightSidebarPresenter>(() => ({
    activeView: ActiveSidebarView.Empty,
    selectedEvent: null,
    participantsInfo: [],
    accounts: [],

    get title(): string {
      return presenter.activeView ? TITLES[presenter.activeView] : '';
    },

    get hasBackButton(): boolean {
      return presenter.isEdit;
    },

    get isInfo(): boolean {
      return presenter.activeView === ActiveSidebarView.Info;
    },

    get isEdit(): boolean {
      return presenter.activeView === ActiveSidebarView.Edit;
    },

    get isCreate(): boolean {
      return presenter.activeView === ActiveSidebarView.Create;
    },

    setAuthEmails(accounts: ICalendarAuthAccount[]): void {
      presenter.accounts = accounts;
    },

    setActiveView(view: ActiveSidebarView): void {
      presenter.activeView = view;
    },

    setEvent(selectedEvent: IEventCalendar): void {
      presenter.selectedEvent = selectedEvent;
    },

    goBack(): void {
      props.onInfo();
    },

    onClose(): void {
      props.onClose();
    },

    onEdit(): void {
      props.onEdit();
    },

    async onDelete(): Promise<void> {
      if (!presenter.selectedEvent) {
        return;
      }

      if (presenter.selectedEvent.recurrenceType !== RepeatEnum.NONE) {
        const canClose = await popupsTasks.showConfirmWithCheckbox({
          text: t('calendar_delete_confirm_recurring'),
          checkBoxText: t('calendar_delete_series_checkbox'),
          primaryText: t('calendar_delete'),
          secondaryText: t('calendar_cancel'),
        });

        if (!canClose.isConfirmed) {
          return;
        }

        if (canClose.isChecked) {
          const isDeleted = await Tasks.calendar.deleteRecurrenceCalendarEvent({
            parentId: presenter.selectedEvent.parentId,
            account: presenter.selectedEvent.account,
          });
          if (isDeleted) {
            props.deleteEventsAfterDeleteRecurrenceSuccess(presenter.selectedEvent.parentId);
          }
        } else {
          const isDeleted = await Tasks.calendar.deleteCalendarEvent({
            providerEventId: presenter.selectedEvent.providerEventId,
            account: presenter.selectedEvent.account,
          });
          if (isDeleted) {
            props.deleteEventAfterDeleteSuccess(presenter.selectedEvent.providerEventId);
          }
        }
      } else {
        const canDelete = await popupsTasks.showConfirm({
          text: t('calendar_delete_confirm_single'),
          primaryText: t('calendar_delete'),
          secondaryText: t('calendar_cancel'),
        });

        if (!canDelete) {
          return;
        }

        const isDeleted = await Tasks.calendar.deleteCalendarEvent({
          providerEventId: presenter.selectedEvent.providerEventId,
          account: presenter.selectedEvent.account,
        });

        if (isDeleted) {
          props.deleteEventAfterDeleteSuccess(presenter.selectedEvent.providerEventId);
        }
      }
    },

    async onCreateEvent(params: ICalendarEventSubmitData): Promise<void> {
      const finalParams: ICreateCalendarEventParams = {
        title: params.title,
        description: params.description,
        startTimeTs: params.allDay
          ? BigInt(dayjs(params.start).utc(true).valueOf())
          : BigInt(dayjs(params.start).valueOf()),
        endTimeTs: params.allDay
          ? BigInt(
              dayjs(params.end.getTime() + 1)
                .utc(true)
                .valueOf()
            )
          : BigInt(dayjs(params.end).valueOf()),
        allDay: params.allDay,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        recurrenceType: params.repeat,
        callLink: params.callLink,
        location: '',
        chatLink: '',
        reminders: params.reminders
          .filter((reminder) => Boolean(reminder.value))
          .map((reminder) => ({
            type: ReminderType.EMAIL,
            time: BigInt(Number(reminder.value)),
          })),
        participantEmails: params.participants,
        account: {
          type: CalendarType.GOOGLE,
          providerEmail: presenter.accounts[0].account.providerEmail || '',
        },
      };

      const createdEvent = await Tasks.calendar.createCalendarEvent(finalParams);

      if (params.repeat !== RepeatEnum.NONE) {
        if (createdEvent) {
          props.addEventsAfterCreateRecurrenceSuccess();
        }
      } else {
        if (createdEvent) {
          props.addEventAfterCreateSuccess(createdEvent);
        }
      }
    },

    async onEditEvent(params: ICalendarEventSubmitData): Promise<void> {
      if (!presenter.selectedEvent) {
        return;
      }

      if (
        presenter.selectedEvent.recurrenceType !== params.repeat ||
        params.repeat !== RepeatEnum.NONE
      ) {
        const finalParams: IUpdateRecurrenceCalendarEventParams = {
          parentId: presenter.selectedEvent.parentId,
          title: params.title,
          description: params.description,
          startTimeTs: params.allDay
            ? BigInt(dayjs(params.start).utc(true).valueOf())
            : BigInt(dayjs(params.start).valueOf()),
          endTimeTs: params.allDay
            ? BigInt(
                dayjs(params.end.getTime() + 1)
                  .utc(true)
                  .valueOf()
              )
            : BigInt(dayjs(params.end).valueOf()),
          allDay: params.allDay,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          recurrenceType: params.repeat,
          callLink: params.callLink,
          location: '',
          chatLink: '',
          reminders: params.reminders
            .filter((reminder) => !!reminder.value)
            .map((reminder) => ({
              type: ReminderType.EMAIL,
              time: BigInt(+reminder.value),
            })),
          participantEmails: params.participants,
          account: presenter.selectedEvent.account,
        };

        props.onEditEvent(finalParams);
      } else {
        const finalParams: IUpdateCalendarEventParams = {
          providerEventId: presenter.selectedEvent.providerEventId,
          title: params.title,
          description: params.description,
          startTimeTs: params.allDay
            ? BigInt(dayjs(params.start).utc(true).valueOf())
            : BigInt(dayjs(params.start).valueOf()),
          endTimeTs: params.allDay
            ? BigInt(
                dayjs(params.end.getTime() + 1)
                  .utc(true)
                  .valueOf()
              )
            : BigInt(dayjs(params.end).valueOf()),
          allDay: params.allDay,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          callLink: params.callLink,
          location: '',
          chatLink: '',
          reminders: params.reminders
            .filter((reminder) => !!reminder.value)
            .map((reminder) => ({
              type: ReminderType.EMAIL,
              time: BigInt(+reminder.value),
            })),
          participantEmails: params.participants,
          account: presenter.selectedEvent.account,
        };
        props.onEditEvent(finalParams);
      }
    },

    async updateParticipantStatus(confirmationType: ConfirmationType): Promise<void> {
      if (!presenter.selectedEvent) {
        return;
      }

      const updateEvent = await Tasks.calendar.updateCalendarEventParticipantStatus({
        providerEventId:
          presenter.selectedEvent.recurrenceType !== RepeatEnum.NONE
            ? presenter.selectedEvent.parentId
            : presenter.selectedEvent.providerEventId,
        confirmationType,
        account: presenter.selectedEvent.account,
      });

      if (updateEvent) {
        props.updateEventAfterUpdateParticipantStatusSuccess(updateEvent);
      }
    },

    async getUsersByEmails(participants: IParticipant[]): Promise<void> {
      const emails = participants.map((participant) => participant.email);

      const participantsInfo = await Tasks.users.getUsersByEmails({
        email: emails,
      });

      runInAction(() => {
        presenter.participantsInfo = participantsInfo;
      });
    },
  }));

  useEffect(() => {
    presenter.setAuthEmails(props.accounts);
  }, [props.accounts]);

  useEffect(() => {
    presenter.setActiveView(props.activeView);
  }, [props.activeView]);

  useEffect(() => {
    if (props.selectedEvent) {
      presenter.setEvent(props.selectedEvent);
    }
  }, [props.selectedEvent]);

  useEffect(() => {
    if (props.templateEvent) {
      presenter.setEvent(props.templateEvent);
    }
  }, [props.templateEvent]);

  useEffect(() => {
    if (props.selectedEvent?.participants) {
      presenter.getUsersByEmails(props.selectedEvent.participants);
    }
  }, [props.selectedEvent?.participants]);

  useEffect(() => {
    if (props.templateEvent?.participants) {
      presenter.getUsersByEmails(props.templateEvent.participants);
    }
  }, [props.templateEvent?.participants]);

  return presenter;
}
